import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { ArrowLeft, Navigation, ExternalLink, Pencil, Copy, Trash2, Loader2, AlertTriangle, Check, X, RotateCcw, Info, CalendarDays, User, MapPin } from 'lucide-react';
import { RECORRIDO_ESTADO, fmtFecha } from './RecorridosPage';
import { buildEtapas, visitaUrl, puntoLabel, waypointLimit, handleMapsClick } from '../lib/googleMapsLinks';
import { fuenteLabel } from '../components/mapa/PointCard';

const API = process.env.REACT_APP_BACKEND_URL + '/api';
const VISITA_TONE = { pendiente: 'text-slate-500', realizada: 'text-emerald-700', no_realizada: 'text-red-600' };
const VISITA_L = { pendiente: 'Pendiente', realizada: 'Realizada', no_realizada: 'No realizada' };

const NavPanel = ({ rec }) => {
  const limit = waypointLimit();
  const etapas = useMemo(() => buildEtapas(rec, limit), [rec, limit]);
  const siguiente = rec.visitas.find(v => v.disponible && v.estado === 'pendiente' && v.lat != null);
  if (!rec.visitas.some(v => v.disponible && v.lat != null && v.lng != null)) {
    return <p className="text-sm text-slate-500 py-3" data-testid="recorrido-sin-visitas-disponibles">No hay visitas disponibles en este recorrido.</p>;
  }
  return (
    <div className="card-base p-4 space-y-3" data-testid="nav-panel">
      {etapas.length === 1 ? (
        <a href={etapas[0].url} target="_blank" rel="noopener noreferrer" onClick={(ev) => handleMapsClick(ev, etapas[0].url)} className="btn-primary text-sm flex items-center justify-center gap-2 py-2.5" data-testid="abrir-recorrido-gmaps">
          <Navigation className="w-4 h-4" /> Abrir recorrido en Google Maps
        </a>
      ) : (
        <div data-testid="etapas-list">
          <p className="text-sm font-medium text-slate-800">Abrir recorrido en Google Maps por etapas ({etapas.length})</p>
          <p className="text-xs text-slate-500 mb-2">Google Maps admite {limit} paradas intermedias por enlace en este dispositivo. Ninguna parada se omite: cada etapa empieza donde terminó la anterior.</p>
          <div className="space-y-1.5">
            {etapas.map((e, i) => (
              <a key={e.url} href={e.url} target="_blank" rel="noopener noreferrer" onClick={(ev) => handleMapsClick(ev, e.url)} className="btn-secondary text-sm flex items-center justify-between gap-2 py-2" data-testid={`etapa-${i + 1}`}>
                <span className="truncate">Etapa {i + 1}: {e.desde} → {e.hasta}</span><ExternalLink className="w-3.5 h-3.5 shrink-0" />
              </a>
            ))}
          </div>
        </div>
      )}
      {siguiente ? (
        <a href={visitaUrl(siguiente)} target="_blank" rel="noopener noreferrer" onClick={(ev) => handleMapsClick(ev, visitaUrl(siguiente))} className="btn-secondary text-sm flex items-center justify-center gap-2 py-2.5" data-testid="ir-siguiente-visita">
          <Navigation className="w-4 h-4" /> Ir a la siguiente visita ({siguiente.orden}. {siguiente.codigo_npl || siguiente.direccion})
        </a>
      ) : <p className="text-xs text-slate-500 text-center" data-testid="sin-visitas-pendientes">No quedan visitas pendientes.</p>}
      <p className="text-[11px] text-slate-500 flex gap-1.5"><Info className="w-3.5 h-3.5 shrink-0 mt-px" />El orden es el guardado en XÉNICOS (no optimizado). Google Maps calcula la conducción entre paradas. «Ir a la siguiente visita» parte de tu ubicación actual.</p>
    </div>
  );
};

const VisitaRow = ({ v, onEstado, busy, historica = false }) => (
  <li className={`card-base p-3 ${!v.disponible ? 'opacity-70' : ''}`} data-testid={`visita-row-${v.visita_id}`}>
    <div className="flex items-start gap-3">
      <span className="w-7 h-7 shrink-0 rounded-full bg-blue-600 text-white text-xs font-semibold flex items-center justify-center">{v.orden}</span>
      <div className="min-w-0 flex-1">
        {v.disponible ? (
          <>
            <Link to={`/activos/${v.asset_id}`} className="text-sm font-semibold text-slate-900 hover:underline" data-testid={`visita-asset-${v.visita_id}`}>{v.codigo_npl || 'Sin código'}</Link>
            <p className="text-sm text-slate-700 break-words">{v.direccion || 'Sin dirección'}{v.municipio ? `, ${v.municipio}` : ''}</p>
            <p className="text-[11px] text-slate-400 flex items-center gap-1"><MapPin className="w-3 h-3" />{fuenteLabel(v.fuente_coord)}</p>
            {v.coords_cambiadas && <p className="text-[11px] text-amber-700 flex items-center gap-1 mt-0.5"><AlertTriangle className="w-3 h-3" />Las coordenadas del activo han cambiado desde que se guardó</p>}
          </>
        ) : <p className="text-sm text-red-600 flex items-center gap-1" data-testid={`visita-no-disponible-${v.visita_id}`}><AlertTriangle className="w-3.5 h-3.5" />Activo no disponible (eliminado o sin permiso)</p>}
        <p className={`text-xs font-medium mt-1 ${VISITA_TONE[v.estado]}`} data-testid={`visita-estado-${v.visita_id}`}>{VISITA_L[v.estado]}</p>
        {historica && <p className="text-xs text-slate-500" data-testid={`visita-historica-${v.visita_id}`}>Activo cerrado · visita conservada en el histórico</p>}
      </div>
    </div>
    {v.disponible && !historica && (
      <div className="flex flex-wrap gap-1.5 mt-2.5">
        <a href={visitaUrl(v)} target="_blank" rel="noopener noreferrer" onClick={(ev) => handleMapsClick(ev, visitaUrl(v))} className="btn-secondary text-xs px-2.5 py-1.5 flex items-center gap-1" data-testid={`abrir-visita-gmaps-${v.visita_id}`}><ExternalLink className="w-3 h-3" /> Abrir esta visita en Google Maps</a>
        {v.estado !== 'realizada' && <button disabled={busy} onClick={() => onEstado(v, 'realizada')} className="btn-secondary text-xs px-2.5 py-1.5 flex items-center gap-1 text-emerald-700" data-testid={`visita-realizada-${v.visita_id}`}><Check className="w-3 h-3" /> Realizada</button>}
        {v.estado !== 'no_realizada' && <button disabled={busy} onClick={() => onEstado(v, 'no_realizada')} className="btn-secondary text-xs px-2.5 py-1.5 flex items-center gap-1 text-red-600" data-testid={`visita-no-realizada-${v.visita_id}`}><X className="w-3 h-3" /> No realizada</button>}
        {v.estado !== 'pendiente' && <button disabled={busy} onClick={() => onEstado(v, 'pendiente')} className="btn-secondary text-xs px-2.5 py-1.5 flex items-center gap-1" data-testid={`visita-pendiente-${v.visita_id}`}><RotateCcw className="w-3 h-3" /> Pendiente</button>}
      </div>
    )}
  </li>
);

const Avisos = ({ rec, onActualizar, busy }) => {
  const a = rec.avisos || {};
  if (!a.no_disponibles && !a.coords_cambiadas && !a.mediador_sin_acceso) return null;
  return (
    <div className="rounded-lg border border-amber-200 bg-amber-50 p-3 text-sm text-amber-700 space-y-1" data-testid="recorrido-avisos">
      {a.no_disponibles > 0 && <p>{a.no_disponibles} visita(s) corresponden a activos que ya no están disponibles para ti.</p>}
      {a.coords_cambiadas > 0 && <p>{a.coords_cambiadas} activo(s) han cambiado de coordenadas desde que se guardó el recorrido.</p>}
      {a.mediador_sin_acceso > 0 && <p>El mediador responsable no tiene asignados {a.mediador_sin_acceso} de los activos: no podrá ver su ficha ni gestionarlos hasta que se le asignen.</p>}
      {rec.puede_editar && (a.no_disponibles > 0 || a.coords_cambiadas > 0) && (
        <button disabled={busy} onClick={onActualizar} className="btn-secondary text-xs px-2.5 py-1.5 mt-1" data-testid="recorrido-actualizar">
          {a.no_disponibles > 0 ? 'Retirar no disponibles y actualizar coordenadas' : 'Actualizar coordenadas'}
        </button>
      )}
    </div>
  );
};

export default function RecorridoDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [rec, setRec] = useState(null);
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => {
    axios.get(`${API}/recorridos/${id}`, { withCredentials: true })
      .then(r => setRec(r.data))
      .catch(err => setError(err.response?.data?.detail || 'No se pudo cargar el recorrido'));
  }, [id]);
  useEffect(() => {
    load();
    window.addEventListener('xenicos:recorridos-changed', load);
    window.addEventListener('xenicos:notifications-refreshed', load);
    window.addEventListener('focus', load);
    return () => {
      window.removeEventListener('xenicos:recorridos-changed', load);
      window.removeEventListener('xenicos:notifications-refreshed', load);
      window.removeEventListener('focus', load);
    };
  }, [load]);

  const run = async (fn, okMsg) => {
    setBusy(true);
    try { await fn(); if (okMsg) toast.success(okMsg); load(); } catch (err) {
      const d = err.response?.data?.detail;
      toast.error(typeof d === 'object' ? `${d.message}: ${(d.problemas || []).join(' · ')}` : (d || 'Operación no completada'));
    } finally { setBusy(false); }
  };

  const setVisita = (v, estado) => run(() => axios.patch(`${API}/recorridos/${id}/visitas/${v.visita_id}`, { estado }, { withCredentials: true }));
  const setEstado = (estado) => run(() => axios.patch(`${API}/recorridos/${id}/estado`, { estado }, { withCredentials: true }), 'Estado actualizado');
  const actualizar = () => run(() => axios.put(`${API}/recorridos/${id}`, {
    nombre: rec.nombre, fecha_prevista: rec.fecha_prevista, mediador_id: rec.mediador_id, salida: rec.salida,
    regreso: rec.regresar_a_salida ? null : rec.regreso, regresar_a_salida: rec.regresar_a_salida,
    visitas: rec.visitas.filter(v => v.disponible).map(v => ({ asset_id: v.asset_id, visita_id: v.visita_id })),
  }, { withCredentials: true }), 'Recorrido actualizado');
  const eliminar = async () => {
    if (!window.confirm('¿Eliminar este recorrido? Las visitas pendientes quedarán registradas como canceladas en cada activo.')) return;
    setBusy(true);
    try { await axios.delete(`${API}/recorridos/${id}`, { withCredentials: true }); toast.success('Recorrido eliminado'); navigate('/recorridos'); } catch (err) { toast.error(err.response?.data?.detail || 'No se pudo eliminar'); setBusy(false); }
  };

  if (error) return <div className="card-base p-8 text-center text-slate-600" data-testid="recorrido-error">{error} · <Link to="/recorridos" className="text-blue-700 underline">Volver</Link></div>;
  if (!rec) return <div className="flex justify-center py-16"><Loader2 className="w-6 h-6 animate-spin text-slate-400" /></div>;
  const est = RECORRIDO_ESTADO[rec.estado] || RECORRIDO_ESTADO.planificado;

  return (
    <div className="space-y-4 max-w-3xl" data-testid="recorrido-detail">
      <div className="flex items-start gap-3">
        <Link to="/recorridos" className="p-2 hover:bg-slate-100 rounded-lg mt-0.5" data-testid="recorrido-back"><ArrowLeft className="w-5 h-5" /></Link>
        <div className="min-w-0 flex-1">
          <h1 className="heading-1 break-words" data-testid="recorrido-nombre-titulo">{rec.nombre}</h1>
          <div className="flex flex-wrap items-center gap-x-4 gap-y-1 mt-1 text-sm text-slate-600">
            <span className="flex items-center gap-1"><CalendarDays className="w-4 h-4" />{fmtFecha(rec.fecha_prevista)}</span>
            <span className="flex items-center gap-1"><User className="w-4 h-4" />{rec.mediador_nombre}</span>
            <span className={`text-[11px] px-2 py-0.5 rounded border ${est.tone}`} data-testid="recorrido-estado-badge">{est.l}</span>
          </div>
          <p className="text-xs text-slate-400 mt-1">Creado por {rec.creador_nombre}</p>
        </div>
      </div>
      <div className="flex flex-wrap gap-2">
        <select value={rec.estado} onChange={e => setEstado(e.target.value)} disabled={busy} className="input-base text-sm py-1.5" style={{ width: "auto" }} data-testid="recorrido-estado-select">
          {Object.entries(RECORRIDO_ESTADO).map(([k, v]) => <option key={k} value={k}>{v.l}</option>)}
        </select>
        {rec.puede_editar && <Link to={`/mapa?recorrido=${rec.id}`} className="btn-secondary text-sm flex items-center gap-1.5" data-testid="recorrido-editar"><Pencil className="w-3.5 h-3.5" /> Editar</Link>}
        <Link to={`/mapa?duplicar=${rec.id}`} className="btn-secondary text-sm flex items-center gap-1.5" data-testid="recorrido-duplicar"><Copy className="w-3.5 h-3.5" /> Duplicar</Link>
        {rec.puede_editar && <button onClick={eliminar} disabled={busy} className="btn-secondary text-sm flex items-center gap-1.5 text-red-600" data-testid="recorrido-eliminar"><Trash2 className="w-3.5 h-3.5" /> Eliminar</button>}
      </div>
      <Avisos rec={rec} onActualizar={actualizar} busy={busy} />
      <NavPanel rec={rec} />
      <div className="card-base p-4 text-sm space-y-1" data-testid="recorrido-puntos">
        <p><span className="text-slate-500">Salida:</span> <span className="text-slate-900">{puntoLabel(rec.salida)}</span></p>
        <p><span className="text-slate-500">Regreso:</span> <span className="text-slate-900">{rec.regresar_a_salida ? 'Mismo punto de salida' : puntoLabel(rec.regreso)}</span></p>
      </div>
      <ol className="space-y-2" data-testid="recorrido-visitas">
        {rec.visitas.map(v => <VisitaRow key={v.visita_id} v={v} onEstado={setVisita} busy={busy} />)}
      </ol>
      {(rec.visitas_historicas || []).length > 0 && <details className="border-t border-slate-200 pt-4" data-testid="recorrido-historico">
        <summary className="text-sm font-medium cursor-pointer" data-testid="recorrido-historico-toggle">Histórico de visitas ({rec.visitas_historicas.length})</summary>
        <ol className="space-y-2 mt-3">{rec.visitas_historicas.map(v => <VisitaRow key={v.visita_id} v={v} historica />)}</ol>
      </details>}
    </div>
  );
}
